import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onPlayAudio: () => void;
}

export default function HeroSection({ onPlayAudio }: HeroSectionProps) {
  return (
    <section className="relative min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-primary/10 via-background to-accent/10">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(95,189,176,0.3),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,138,101,0.3),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-6 text-center lg:text-left">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
              <span className="text-sm font-semibold text-primary">India's #1 Audio Storytelling Platform</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-text-primary leading-tight">
              Your Stories, Your Language,{' '}
              <span className="bg-gradient-to-r from-purple-700 via-pink-600 to-orange-500 bg-clip-text text-transparent">Your World</span>
            </h1>

            <p className="text-lg md:text-xl text-text-secondary max-w-2xl mx-auto lg:mx-0">
              Discover thousands of captivating audio stories in Hindi, Tamil, Telugu, Bengali, and 15+ Indian languages. From romance to thriller, comedy to drama - your perfect story awaits.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                onClick={onPlayAudio}
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-cta text-cta-foreground rounded-xl font-cta font-bold text-lg transition-all duration-300 hover:bg-cta/90 hover:shadow-warm-lg hover:scale-105">

                <Icon name="PlayIcon" size={24} variant="solid" />
                <span>Listen to Featured Story</span>
              </button>

              <a
                href="/app-download"
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-primary text-primary-foreground rounded-xl font-cta font-bold text-lg transition-all duration-300 hover:bg-primary-dark hover:shadow-warm-lg hover:scale-105">

                <Icon name="ArrowDownTrayIcon" size={24} />
                <span>Download App</span>
              </a>
            </div>

            <div className="flex items-center justify-center lg:justify-start space-x-8 pt-4">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary">10L+</p>
                <p className="text-sm text-text-secondary">Active Listeners</p>
              </div>
              <div className="h-12 w-px bg-border" />
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary">50K+</p>
                <p className="text-sm text-text-secondary">Audio Stories</p>
              </div>
              <div className="h-12 w-px bg-border" />
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-primary">18+</p>
                <p className="text-sm text-text-secondary">Languages</p>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-square max-w-lg mx-auto">
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-full opacity-20 blur-3xl animate-pulse" />
              
              <div className="relative bg-card rounded-3xl shadow-warm-lg p-6 md:p-8">
                <div className="aspect-video rounded-2xl overflow-hidden mb-6 relative">
                  <AppImage
                    src="https://img.rocket.new/generatedImages/rocket_gen_img_17f8f5ea4-1763299033127.png"
                    alt="Young Indian woman wearing headphones smiling while listening to audio stories on smartphone in cozy home setting"
                    className="w-full h-full object-cover" />

                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading font-bold text-lg text-text-primary">Dil Ki Baatein</h3>
                      <p className="text-sm text-text-secondary">Romance • Hindi • 45 min</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="StarIcon" size={16} variant="solid" className="text-accent" />
                      <span className="text-sm font-semibold text-text-primary">4.8</span>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                      <div className="h-full w-1/3 bg-primary rounded-full" />
                    </div>
                    <span className="text-xs text-text-secondary">15:30</span>
                  </div>

                  <div className="flex items-center justify-center space-x-6">
                    <button className="p-2 rounded-full hover:bg-muted transition-colors">
                      <Icon name="BackwardIcon" size={24} className="text-text-secondary" />
                    </button>
                    <button
                      onClick={onPlayAudio}
                      className="p-4 bg-primary rounded-full hover:bg-primary-dark transition-all hover:scale-110">

                      <Icon name="PlayIcon" size={28} variant="solid" className="text-primary-foreground" />
                    </button>
                    <button className="p-2 rounded-full hover:bg-muted transition-colors">
                      <Icon name="ForwardIcon" size={24} className="text-text-secondary" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>);

}